import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { VolumeUp as VolumeUpIcon, VolumeOff as VolumeOffIcon } from '@mui/icons-material';
import { claimAudioFocus, releaseAudioFocus } from '../../utils/audioFocus';

export interface NarrationControlRef {
  /** Plays the given audio URL — a no-op if narration is currently muted (FR-011). */
  play: (audioUrl: string) => void;
  /** Stops any in-progress narration playback (used by the FR-012 stop control, T046a). */
  stop: () => void;
}

/**
 * Mute/enable toggle for AI narration, defaulting to muted (spec 044 FR-011) — narration
 * only plays once the user explicitly taps this control, and stays enabled for the rest of
 * the session (in-memory state here; not persisted across reloads, per spec Assumptions).
 */
const NarrationControl = forwardRef<NarrationControlRef>((_props, ref) => {
  const [enabled, setEnabled] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const stopPlayback = () => {
    audioRef.current?.pause();
    audioRef.current = null;
  };

  useImperativeHandle(ref, () => ({
    play: (audioUrl: string) => {
      if (!enabled) return;
      // Spec 046 FR-018: audio never overlaps — claiming focus stops any other speech first.
      claimAudioFocus(stopPlayback);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.onended = () => releaseAudioFocus(stopPlayback);
      audio.play().catch(() => {
        // Autoplay may still be blocked by the browser even after a prior user gesture
        // enabled narration; fail silently rather than surfacing a broken-audio error.
      });
    },
    stop: () => {
      stopPlayback();
      releaseAudioFocus(stopPlayback);
    },
  }), [enabled]);

  return (
    <Tooltip title={enabled ? 'Narration on — click to mute' : 'Narration off — click to enable voice explanations'}>
      <IconButton size="small" onClick={() => setEnabled((v) => !v)} color={enabled ? 'primary' : 'default'}>
        {enabled ? <VolumeUpIcon fontSize="small" /> : <VolumeOffIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
});

NarrationControl.displayName = 'NarrationControl';

export default NarrationControl;
